import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  AccountBalance,
  CheckCircle,
  Warning,
  Error,
  Download,
  Upload,
  Refresh,
  Schedule,
  AttachMoney,
  Assessment,
  Send,
  Cancel,
  Visibility,
  ThumbUp,
  ThumbDown,
  Timer,
  Payment,
  TrendingUp,
  FileDownload,
  PendingActions,
  CheckBox,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { settlementService } from '../services/api';

interface Settlement {
  id: string;
  batchId: string;
  type: 'MERCHANT' | 'RETAILER' | 'AGENT';
  partnerName: string;
  partnerId: string;
  amount: number;
  currency: string;
  transactionCount: number;
  commissionAmount: number;
  netAmount: number;
  status: 'PENDING' | 'PROCESSING' | 'APPROVED' | 'REJECTED' | 'COMPLETED' | 'FAILED';
  createdDate: string;
  scheduledDate: string;
  completedDate?: string;
  bankAccount: string;
  reference: string;
  approvalRequired: boolean;
  approvedBy?: string;
  rejectionReason?: string;
}

const SettlementManagement: React.FC = () => {
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState<Settlement | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [batchDialog, setBatchDialog] = useState(false);
  const [selectedSettlements, setSelectedSettlements] = useState<string[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Statistics (computed from the fetched settlements)
  const [stats, setStats] = useState({
    pendingAmount: 0,
    pendingCount: 0,
    processingAmount: 0,
    processingCount: 0,
    completedAmount: 0,
    completedCount: 0,
    failedAmount: 0,
    failedCount: 0,
  });

  useEffect(() => {
    fetchSettlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map a backend SettlementResponse onto the page's Settlement shape.
  const mapSettlement = (s: any): Settlement => {
    const backendStatus: string = s.status || 'DRAFT';
    const statusMap: Record<string, Settlement['status']> = {
      DRAFT: 'PENDING',
      PENDING_APPROVAL: 'PENDING',
      APPROVED: 'APPROVED',
      PROCESSING: 'PROCESSING',
      PARTIALLY_COMPLETED: 'PROCESSING',
      COMPLETED: 'COMPLETED',
      FAILED: 'FAILED',
      CANCELLED: 'REJECTED',
    };
    const type: Settlement['type'] = (s.settlementType || '').includes('MERCHANT')
      ? 'MERCHANT'
      : (s.settlementType || '').includes('RETAILER')
      ? 'RETAILER'
      : 'AGENT';
    return {
      id: s.id?.toString() || s.batchNumber,
      batchId: s.batchNumber,
      type,
      partnerName: s.partnerName || '-',
      partnerId: s.partnerCode || s.partnerId || '',
      amount: Number(s.grossAmount || 0),
      currency: s.currency || 'USD',
      transactionCount: s.transactionCount || 0,
      commissionAmount: Number(s.commissionAmount || 0),
      netAmount: Number(s.netAmount || 0),
      status: statusMap[backendStatus] || 'PENDING',
      createdDate: s.createdAt || s.settlementDate || new Date().toISOString(),
      scheduledDate: s.settlementDate || s.createdAt || new Date().toISOString(),
      completedDate: s.completedAt,
      bankAccount: s.bankAccountNumber || '',
      reference: s.batchNumber,
      approvalRequired: backendStatus === 'DRAFT' || backendStatus === 'PENDING_APPROVAL',
      approvedBy: s.approvedBy,
      rejectionReason: s.failureReason,
    };
  };

  const computeStats = (list: Settlement[]) => {
    const next = {
      pendingAmount: 0, pendingCount: 0,
      processingAmount: 0, processingCount: 0,
      completedAmount: 0, completedCount: 0,
      failedAmount: 0, failedCount: 0,
    };
    for (const s of list) {
      if (s.status === 'PENDING') { next.pendingAmount += s.netAmount; next.pendingCount += s.transactionCount; }
      else if (s.status === 'APPROVED' || s.status === 'PROCESSING') { next.processingAmount += s.netAmount; next.processingCount += s.transactionCount; }
      else if (s.status === 'COMPLETED') { next.completedAmount += s.netAmount; next.completedCount += s.transactionCount; }
      else if (s.status === 'FAILED' || s.status === 'REJECTED') { next.failedAmount += s.netAmount; next.failedCount += s.transactionCount; }
    }
    return next;
  };

  const fetchSettlements = async () => {
    setLoading(true);
    try {
      const data = await settlementService.getSettlements({ page: 0, size: 200 });
      const content: any[] = data?.content ?? (Array.isArray(data) ? data : []);
      const mapped = content.map(mapSettlement);
      setSettlements(mapped);
      setStats(computeStats(mapped));
    } catch (error) {
      console.error('Failed to fetch settlements:', error);
      setSettlements([]);
      setStats(computeStats([]));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBatch = async () => {
    try {
      await settlementService.createSettlementBatch();
      setBatchDialog(false);
      await fetchSettlements();
    } catch (error) {
      console.error('Failed to create batch:', error);
      alert('Failed to create settlement batch.');
    }
  };

  const handleApprove = async () => {
    if (!selectedSettlement) return;
    try {
      await settlementService.approveSettlement(selectedSettlement.batchId, approvalComment);
      setApprovalDialog(false);
      setApprovalComment('');
      await fetchSettlements();
    } catch (error) {
      console.error('Failed to approve settlement:', error);
      alert('Failed to approve settlement.');
    }
  };

  const handleReject = async () => {
    if (!selectedSettlement || !rejectionReason) {
      alert('Please provide a rejection reason');
      return;
    }
    try {
      await settlementService.rejectSettlement(selectedSettlement.batchId, rejectionReason);
      setApprovalDialog(false);
      setRejectionReason('');
      await fetchSettlements();
    } catch (error) {
      console.error('Failed to reject settlement:', error);
      alert('Failed to reject settlement.');
    }
  };

  // Process an approved settlement (posts the journal, applies the balance changes, runs delivery).
  const handleProcess = async (settlement: Settlement) => {
    try {
      await settlementService.processSettlement(settlement.batchId);
      await fetchSettlements();
    } catch (error) {
      console.error('Failed to process settlement:', error);
      alert('Failed to process settlement.');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedSettlements.length === 0) {
      alert('Please select settlements to approve');
      return;
    }
    try {
      const batchIds = settlements
        .filter(s => selectedSettlements.includes(s.id))
        .map(s => s.batchId);
      await Promise.all(batchIds.map(b => settlementService.approveSettlement(b)));
      setSelectedSettlements([]);
      await fetchSettlements();
    } catch (error) {
      console.error('Failed to bulk approve:', error);
      alert('Failed to bulk approve settlements.');
    }
  };

  const handleDownloadFile = async (settlement: Settlement, format: 'csv' | 'pain001') => {
    try {
      const blob = await settlementService.downloadSettlementFile(settlement.batchId, format);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${settlement.batchId}.${format === 'csv' ? 'csv' : 'xml'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Download is not available for this settlement.');
    }
  };

  const getStatusColor = (status: Settlement['status']) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'PROCESSING': return 'info';
      case 'APPROVED': return 'primary';
      case 'COMPLETED': return 'success';
      case 'REJECTED': return 'error';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: Settlement['status']) => {
    switch (status) {
      case 'PENDING': return <Timer />;
      case 'PROCESSING': return <Schedule />;
      case 'APPROVED': return <ThumbUp />;
      case 'COMPLETED': return <CheckCircle />;
      case 'REJECTED': return <ThumbDown />;
      case 'FAILED': return <Error />;
      default: return null;
    }
  };

  // Tab filter for the table (badges/stats are computed from the full list).
  const visibleSettlements = settlements.filter((s) => {
    if (tabValue === 1) return s.status === 'PENDING';
    if (tabValue === 2) return s.status === 'PROCESSING' || s.status === 'APPROVED';
    if (tabValue === 3) return s.status === 'COMPLETED';
    return true; // tab 0: all
  });

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Settlement Management</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchSettlements}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AccountBalance />}
            onClick={() => setBatchDialog(true)}
          >
            Create Batch
          </Button>
          {selectedSettlements.length > 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleBulkApprove}
            >
              Bulk Approve ({selectedSettlements.length})
            </Button>
          )}
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Pending Settlements
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    ${stats.pendingAmount.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats.pendingCount} transactions
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.light' }}>
                  <PendingActions />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Processing
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    ${stats.processingAmount.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats.processingCount} transactions
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.light' }}>
                  <Schedule />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Completed (30d)
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    ${stats.completedAmount.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats.completedCount} transactions
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.light' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Failed
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error">
                    ${stats.failedAmount.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats.failedCount} transactions
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.light' }}>
                  <Error />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab
            label="All Settlements"
            icon={<Badge badgeContent={settlements.length} color="primary" />}
          />
          <Tab
            label="Pending Approval"
            icon={<Badge badgeContent={settlements.filter(s => s.status === 'PENDING').length} color="warning" />}
          />
          <Tab
            label="Processing"
            icon={<Badge badgeContent={settlements.filter(s => s.status === 'PROCESSING').length} color="info" />}
          />
          <Tab
            label="Completed"
            icon={<Badge badgeContent={settlements.filter(s => s.status === 'COMPLETED').length} color="success" />}
          />
        </Tabs>
      </Paper>

      {/* Settlements Table */}
      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedSettlements.length > 0 && selectedSettlements.length < visibleSettlements.length}
                    checked={visibleSettlements.length > 0 && selectedSettlements.length === visibleSettlements.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedSettlements(visibleSettlements.map(s => s.id));
                      } else {
                        setSelectedSettlements([]);
                      }
                    }}
                  />
                </TableCell>
                <TableCell>Batch ID</TableCell>
                <TableCell>Partner</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Gross Amount</TableCell>
                <TableCell align="right">Commission</TableCell>
                <TableCell align="right">Net Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleSettlements.map((settlement) => (
                <TableRow key={settlement.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedSettlements.includes(settlement.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSettlements([...selectedSettlements, settlement.id]);
                        } else {
                          setSelectedSettlements(selectedSettlements.filter(id => id !== settlement.id));
                        }
                      }}
                      disabled={settlement.status !== 'PENDING'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {settlement.batchId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{settlement.partnerName}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {settlement.partnerId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={settlement.type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {settlement.currency} {settlement.amount.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {settlement.transactionCount} txns
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {settlement.currency} {settlement.commissionAmount.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="primary">
                      {settlement.currency} {settlement.netAmount.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={settlement.status}
                      color={getStatusColor(settlement.status) as any}
                      size="small"
                      icon={getStatusIcon(settlement.status) as any}
                    />
                    {settlement.approvalRequired && settlement.status === 'PENDING' && (
                      <Chip
                        label="Requires Approval"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(settlement.scheduledDate), 'MMM dd, HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedSettlement(settlement);
                            setDetailsOpen(true);
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {settlement.status === 'PENDING' && settlement.approvalRequired && (
                        <Tooltip title="Approve/Reject">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => {
                              setSelectedSettlement(settlement);
                              setApprovalDialog(true);
                            }}
                          >
                            <PendingActions fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {settlement.status === 'APPROVED' && (
                        <Tooltip title="Process Settlement">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleProcess(settlement)}
                          >
                            <Send fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {settlement.status === 'COMPLETED' && (
                        <>
                          <Tooltip title="Download mark-off CSV">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadFile(settlement, 'csv')}
                            >
                              <FileDownload fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Settlement Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Settlement Details
          {selectedSettlement && (
            <Chip
              label={selectedSettlement.status}
              color={getStatusColor(selectedSettlement.status) as any}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedSettlement && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">
                  Batch ID
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedSettlement.batchId}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">
                  Reference
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedSettlement.reference}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">
                  Partner
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedSettlement.partnerName} ({selectedSettlement.partnerId})
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">
                  Bank Account
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedSettlement.bankAccount}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="textSecondary">
                  Gross Amount
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedSettlement.currency} {selectedSettlement.amount.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="textSecondary">
                  Commission
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedSettlement.currency} {selectedSettlement.commissionAmount.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="textSecondary">
                  Net Amount
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="primary" gutterBottom>
                  {selectedSettlement.currency} {selectedSettlement.netAmount.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">
                  Created Date
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {format(new Date(selectedSettlement.createdDate), 'PPpp')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">
                  Scheduled Date
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {format(new Date(selectedSettlement.scheduledDate), 'PPpp')}
                </Typography>
              </Grid>
              {selectedSettlement.completedDate && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Completed Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {format(new Date(selectedSettlement.completedDate), 'PPpp')}
                  </Typography>
                </Grid>
              )}
              {selectedSettlement.approvedBy && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Approved By
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedSettlement.approvedBy}
                  </Typography>
                </Grid>
              )}
              {selectedSettlement.rejectionReason && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <Typography variant="caption">Rejection Reason:</Typography>
                    <Typography variant="body2">{selectedSettlement.rejectionReason}</Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialog}
        onClose={() => setApprovalDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Settlement Approval
        </DialogTitle>
        <DialogContent>
          {selectedSettlement && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                You are about to approve/reject a settlement of{' '}
                <strong>
                  {selectedSettlement.currency} {selectedSettlement.netAmount.toLocaleString()}
                </strong>{' '}
                for <strong>{selectedSettlement.partnerName}</strong>
              </Alert>
              <TextField
                fullWidth
                label="Approval Comments (Optional)"
                multiline
                rows={3}
                value={approvalComment}
                onChange={(e) => setApprovalComment(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Rejection Reason (Required for Rejection)"
                multiline
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<ThumbDown />}
            onClick={handleReject}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<ThumbUp />}
            onClick={handleApprove}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Batch Dialog */}
      <Dialog
        open={batchDialog}
        onClose={() => setBatchDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Settlement Batch</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will create settlement batches for all eligible partners based on their settlement frequency.
          </Alert>
          <Stepper orientation="vertical" activeStep={0}>
            <Step>
              <StepLabel>Review Pending Transactions</StepLabel>
              <StepContent>
                <Typography>125 transactions totaling $65,000 are ready for settlement.</Typography>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Select Partners</StepLabel>
              <StepContent>
                <Typography>All eligible partners will be included in the batch.</Typography>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Generate Settlement Files</StepLabel>
              <StepContent>
                <Typography>CSV and PAIN.001 files will be generated.</Typography>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateBatch}>
            Create Batch
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettlementManagement;